// src/ryan/admin/components/RyanCuratedAccessPicker.tsx
//
// Seletor de campos curados pra uma conta VIP/Editor — reaproveitado nos
// modais de VIP e Editor de RyanAdminAccounts.tsx (mesmos campos vivem em
// RyanAccess independente de isVip/isEditor, então um componente só serve
// os dois). Seleção sempre individual (allowedSpaceIds) — o antigo atalho
// "período inteiro" (allowedPeriods) foi removido junto com o sistema de
// organização por período; os campos aparecem agrupados pelos RyanSpaceGroup
// só pra facilitar achar o campo certo, sem nenhum efeito de seleção em bloco.
import React, { useEffect, useMemo, useState } from 'react';
import './RyanCuratedAccessPicker.css';

const tok = () => localStorage.getItem('accessToken') || '';
const NO_GROUP_KEY = '__none__';

interface PickerSpace {
    _id: string;
    name: string;
    groupId: string | null;
    active: boolean;
}

interface PickerGroup {
    _id: string;
    name: string;
    order: number;
}

export interface CuratedAccessValue {
    curatedAccessEnabled: boolean;
    allowedSpaceIds: string[];
}

export const emptyCuratedAccess = (): CuratedAccessValue => ({
    curatedAccessEnabled: false, allowedSpaceIds: [],
});

interface Props {
    value: CuratedAccessValue;
    onChange: (next: CuratedAccessValue) => void;
}

const RyanCuratedAccessPicker: React.FC<Props> = ({ value, onChange }) => {
    const [spaces, setSpaces] = useState<PickerSpace[]>([]);
    const [groups, setGroups] = useState<PickerGroup[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            fetch('/api/ryan/admin/spaces', { headers: { Authorization: `Bearer ${tok()}` } }).then(r => r.ok ? r.json() : []),
            fetch('/api/ryan/admin/space-groups', { headers: { Authorization: `Bearer ${tok()}` } }).then(r => r.ok ? r.json() : []),
        ])
            .then(([spaceList, groupList]: [PickerSpace[], PickerGroup[]]) => {
                setSpaces(Array.isArray(spaceList) ? spaceList.filter(s => s.active) : []);
                setGroups(Array.isArray(groupList) ? groupList : []);
            })
            .finally(() => setLoading(false));
    }, []);

    const sections = useMemo(() => {
        const byGroup = new Map<string, PickerSpace[]>();
        for (const s of spaces) {
            const key = s.groupId || NO_GROUP_KEY;
            if (!byGroup.has(key)) byGroup.set(key, []);
            byGroup.get(key)!.push(s);
        }
        return [
            ...groups.map(g => ({ id: g._id, name: g.name })),
            { id: NO_GROUP_KEY, name: 'Sem Grupo' },
        ].map(({ id, name }) => ({ id, name, spaces: byGroup.get(id) || [] }));
    }, [spaces, groups]);

    const toggleSpace = (id: string) => {
        const next = value.allowedSpaceIds.includes(id)
            ? value.allowedSpaceIds.filter(x => x !== id)
            : [...value.allowedSpaceIds, id];
        onChange({ ...value, allowedSpaceIds: next });
    };

    return (
        <div className="rcap-root">
            <label className="rcap-toggle-row">
                <input
                    type="checkbox" checked={value.curatedAccessEnabled}
                    onChange={e => onChange({ ...value, curatedAccessEnabled: e.target.checked })}
                />
                <span>
                    <strong>Ativar seleção curada de campos</strong>
                    <span className="rcap-toggle-hint">
                        {value.curatedAccessEnabled
                            ? 'Esta conta só vê o que estiver marcado abaixo.'
                            : 'Desativado: esta conta vê todos os campos ativos (padrão).'}
                    </span>
                </span>
            </label>

            {value.curatedAccessEnabled && (
                loading ? (
                    <p className="rcap-hint">Carregando campos…</p>
                ) : (
                    sections.map(section => (
                        <div className="rcap-group" key={section.id}>
                            <div className="rcap-group-label">{section.name}</div>
                            <div className="rcap-space-list">
                                {section.spaces.map(s => (
                                    <label key={s._id} className="rcap-space-row">
                                        <input type="checkbox" checked={value.allowedSpaceIds.includes(s._id)} onChange={() => toggleSpace(s._id)} />
                                        <span className="rcap-space-name">{s.name}</span>
                                    </label>
                                ))}
                                {section.spaces.length === 0 && <p className="rcap-hint">Nenhum campo neste grupo.</p>}
                            </div>
                        </div>
                    ))
                )
            )}
        </div>
    );
};

export default RyanCuratedAccessPicker;
